import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client.js';
import { useFinanceFiltersStore } from '../store/financeFiltersStore.js';

export interface KpiData {
  revenue: number;
  personnelCosts: number;
  estimatedMargin: number;
  pendingPayments: number;
}

export interface MonthlyTrendData {
  month: string;
  revenue: number;
  costs: number;
  cumulativeMargin: number;
}

export interface ExpenseBreakdownItem {
  name: string;
  value: number;
  count: number;
}

export interface TopCollaboratorData {
  id: string;
  name: string;
  avatarUrl: string | null;
  eventCount: number;
  totalFee: number;
  avgFee: number;
  minFee: number;
  maxFee: number;
}

export interface TopPhotographerData {
  id: string;
  name: string;
  eventCount: number;
  totalFee: number;
  avgFee: number;
  minFee: number;
  maxFee: number;
}

export interface PaymentPipelineData {
  month: string;
  depositReceived: number;
  balanceReceived: number;
  pending: number;
}

export interface YearProjectionData {
  ytdRevenue: number;
  projectedRevenue: number;
  prevYearRevenue: number | null;
}

export interface FinanceDashboardData {
  kpi: KpiData;
  monthlyTrend: MonthlyTrendData[];
  expenseBreakdown: ExpenseBreakdownItem[];
  topCollaborators: TopCollaboratorData[];
  topExternalPhotographers: TopPhotographerData[];
  paymentPipeline: PaymentPipelineData[];
  yearProjection: YearProjectionData | null;
}

export function useFinanceDashboard() {
  const from = useFinanceFiltersStore((s) => s.from);
  const to = useFinanceFiltersStore((s) => s.to);

  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);

  return useQuery({
    queryKey: ['finance-dashboard', from, to],
    queryFn: async () => {
      const response = await apiGet<FinanceDashboardData>(
        `/finance/dashboard?${params.toString()}`
      );
      return response;
    },
  });
}
